import React, { useCallback, useEffect, useMemo, useRef } from 'react'
import { StyleSheet, useWindowDimensions, View } from 'react-native'
import { BottomSheetBackdrop, BottomSheetModal, BottomSheetScrollView } from '@gorhom/bottom-sheet'
import { ReduceMotion } from 'react-native-reanimated'
import { isNumber } from 'lodash'
import { DS } from '@/design-system'
import { getDSColorWithOpacity } from '@/design-system/Palette'
import { isWeb, useMaxWidth } from '@/components/WebWrapper'

export interface BlindScheduleRow {
  bigBlind: number
  smallBlind: number
  isCurrent: boolean
  isPast: boolean
  leftLabel: string
}

interface BlindScheduleDrawerProps {
  visible: boolean
  rows: BlindScheduleRow[]
  onClose: () => void
}

export function BlindScheduleDrawer({ visible, rows, onClose }: BlindScheduleDrawerProps) {
  const { width: screenWidth, height: screenHeight } = useWindowDimensions()
  const maxWidth = useMaxWidth()
  const bottomSheetModalRef = useRef<BottomSheetModal>(null)
  const _maxWidth = isNumber(maxWidth) ? maxWidth : screenWidth
  const marginHorizontal = screenWidth > _maxWidth ? (screenWidth - _maxWidth) / 2 : 0
  const snapPoints = useMemo(() => [screenHeight * 0.5], [screenHeight])

  useEffect(() => {
    if (visible) {
      bottomSheetModalRef.current?.present()
      return
    }

    bottomSheetModalRef.current?.dismiss()
  }, [visible])

  const renderBackdrop = useCallback(
    (props: React.ComponentProps<typeof BottomSheetBackdrop>) => (
      <BottomSheetBackdrop {...props} pressBehavior="close" disappearsOnIndex={-1} appearsOnIndex={0} opacity={0.3} />
    ),
    []
  )

  return (
    <BottomSheetModal
      ref={bottomSheetModalRef}
      index={0}
      snapPoints={snapPoints}
      overrideReduceMotion={ReduceMotion.System}
      enableDynamicSizing={false}
      enablePanDownToClose={true}
      enableOverDrag={false}
      detached={false}
      backdropComponent={renderBackdrop}
      handleIndicatorStyle={styles.handleIndicator}
      backgroundStyle={styles.background}
      onDismiss={onClose}
      containerStyle={{
        marginLeft: !isWeb && marginHorizontal > 0 ? marginHorizontal : undefined,
        shadowColor: DS.palette['Shades/Maroon 2'],
        shadowOffset: { width: 0, height: 3 },
        shadowOpacity: 0.27,
        shadowRadius: 4.65,
        elevation: 6
      }}
      style={{
        marginHorizontal: 'auto',
        maxWidth: maxWidth
      }}
    >
      <View style={styles.content}>
        <DS.Text variant="Body Medium/Medium" style={styles.title}>
          Blind Schedule
        </DS.Text>
        <BottomSheetScrollView showsVerticalScrollIndicator contentContainerStyle={styles.scrollContent}>
          {rows.map((row) => (
            <DS.Frame key={row.bigBlind} row style={[styles.blindRow, row.isCurrent && styles.currentBlindRow]}>
              <DS.Text
                variant={row.isCurrent ? 'Body Medium/Medium' : 'Body Medium/Regular'}
                style={[styles.leftLabel, row.isPast && styles.pastBlindText]}
              >
                {row.leftLabel}
              </DS.Text>
              <DS.Text
                variant={row.isCurrent ? 'Body Medium/Medium' : 'Body Medium/Regular'}
                style={[styles.blindValue, row.isPast && styles.pastBlindText]}
              >
                {row.smallBlind}/{row.bigBlind}
              </DS.Text>
            </DS.Frame>
          ))}
        </BottomSheetScrollView>
      </View>
    </BottomSheetModal>
  )
}

const styles = StyleSheet.create({
  handleIndicator: {
    backgroundColor: getDSColorWithOpacity('Global/Black', 0.15)
  },
  background: {
    backgroundColor: DS.palette['Global/White']
  },
  content: {
    flex: 1,
    paddingHorizontal: 24,
    paddingTop: 8,
    paddingBottom: 12
  },
  title: {
    marginBottom: 8,
    textAlign: 'center'
  },
  scrollContent: {
    paddingBottom: 16
  },
  blindRow: {
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'center',
    columnGap: 20,
    paddingVertical: 4,
    borderBottomWidth: 1,
    borderBottomColor: DS.palette['Brand/Light Grey']
  },
  currentBlindRow: {
    backgroundColor: getDSColorWithOpacity('Brand/Yellow', 0.15),
    marginHorizontal: -8,
    paddingHorizontal: 8,
    borderRadius: 4,
    paddingVertical: 4
  },
  pastBlindText: {
    textDecorationLine: 'line-through',
    color: DS.palette['Brand/Dark Grey']
  },
  leftLabel: {
    width: 56,
    textAlign: 'center'
  },
  blindValue: {
    minWidth: 88,
    textAlign: 'center'
  }
})
